import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Check, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LogoMark, BRAND } from "@/components/Logo";
import { useSeo } from "@/lib/seo";
import api from "@/lib/api";

const RULES = [
  { id: "len",   label: "At least 8 characters",         test: (p) => p.length >= 8 },
  { id: "upper", label: "One uppercase letter (A–Z)",     test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "One lowercase letter (a–z)",     test: (p) => /[a-z]/.test(p) },
  { id: "digit", label: "One number (0–9)",               test: (p) => /\d/.test(p) },
  { id: "spec",  label: "One special character (!@#…)",    test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function ChangePassword() {
  useSeo({ title: "Create new password", description: "Set a new password to access the Solemnity Assessment Portal.", path: "/change-password" });
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(user?.must_change_password ? "Solemnity@123" : "");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const forced = !!user?.must_change_password;

  const passed = RULES.map(r => r.test(newPw));
  const allPassed = passed.every(Boolean);
  const matches = newPw === confirmPw && newPw.length > 0;
  const score = passed.filter(Boolean).length;
  const strength = score <= 1 ? "Weak" : score <= 3 ? "Moderate" : score === 4 ? "Strong" : "Excellent";
  const strengthTone = score <= 1 ? "bg-red-500" : score <= 3 ? "bg-amber-500" : score === 4 ? "bg-blue-500" : "bg-emerald-500";

  const submit = async (e) => {
    e.preventDefault();
    if (!allPassed) { toast.error("Password does not meet all requirements"); return; }
    if (!matches) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: newPw });
      const u = await refresh();
      toast.success("Password updated successfully");
      navigate(u ? `/${u.role}` : "/login", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid place-items-center p-6">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2.5 mb-6 justify-center">
          <LogoMark size="sm" />
          <div className="leading-tight">
            <div className="text-base font-semibold text-slate-900">{BRAND.short}</div>
            <div className="text-[10px] tracking-[0.14em] uppercase text-slate-500">Assessment Portal</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-900">
            {forced ? "Create new password" : "Change password"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {forced
              ? "Welcome! Please set a new password to continue to your dashboard."
              : "Update the password used to sign in to the portal."}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {!forced && (
              <Field label="Current password">
                <PasswordInput value={current} onChange={setCurrent} show={show} testId="cp-current" />
              </Field>
            )}

            <Field label="New password">
              <PasswordInput value={newPw} onChange={setNewPw} show={show} testId="cp-new" />
              {newPw && (
                <div className="mt-2">
                  <div className="h-1.5 w-full bg-slate-100 rounded overflow-hidden">
                    <div className={`h-full ${strengthTone} transition-all`} style={{ width: `${(score / 5) * 100}%` }} />
                  </div>
                  <div className="mt-1 text-xs text-slate-500" data-testid="cp-strength">{strength}</div>
                </div>
              )}
            </Field>

            <Field label="Confirm new password">
              <PasswordInput value={confirmPw} onChange={setConfirmPw} show={show} testId="cp-confirm" />
              {confirmPw && (
                <div className={`mt-1 text-xs ${matches ? "text-emerald-600" : "text-red-600"}`}>
                  {matches ? "Passwords match" : "Passwords do not match"}
                </div>
              )}
            </Field>

            <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} className="w-4 h-4 accent-blue-600" />
              {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {show ? "Hide passwords" : "Show passwords"}
            </label>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="text-xs font-medium text-slate-700 uppercase tracking-wider mb-2">
                Your password must contain
              </div>
              <ul className="space-y-1.5">
                {RULES.map((r, i) => (
                  <li key={r.id} className="flex items-center gap-2 text-sm" data-testid={`rule-${r.id}`}>
                    {passed[i] ? (
                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}
                    <span className={passed[i] ? "text-emerald-700" : "text-slate-600"}>{r.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              data-testid="cp-submit"
              type="submit"
              disabled={submitting || !allPassed || !matches}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg px-4 py-3 font-medium shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Updating…" : (<>Update password <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function PasswordInput({ value, onChange, show, testId }) {
  return (
    <input
      data-testid={testId}
      type={show ? "text" : "password"}
      required
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-slate-900 bg-white"
    />
  );
}
